import { visit } from 'unist-util-visit';

interface SourceItem {
  title: string;
  url: string;
}

const SOURCES_HEADER_RE = /^\s*sources\s*:?\s*$/i;

function getNodeText(node: any): string {
  let out = '';
  visit(node, 'text', (n: any) => {
    out += n.value;
  });
  return out;
}

function extractSourceFromListItem(item: any): SourceItem | null {
  let url: string | null = null;
  let title = '';
  visit(item, 'link', (n: any) => {
    if (!url) {
      url = n.url;
      title = getNodeText(n);
    }
  });
  if (!url) return null;
  return { title: title || url, url };
}

// Walks the mdast root and detects `**Sources**` (paragraph) or `## Sources`
// (heading) immediately followed by a list whose items are link-only. When
// found, the list node is rewritten into an `mdSourcesList` element whose
// `items` JSON-prop is consumed by the SourcesList React component below.
// The header itself is left in place so the user still sees the "Sources"
// label — only the bullet list gets transformed into the pill grid.
export const remarkSourcesPlugin = () => (tree: any) => {
  const children = tree.children;
  if (!Array.isArray(children)) return;
  for (let i = 0; i < children.length - 1; i++) {
    const node = children[i];
    const next = children[i + 1];
    if (!node || !next) continue;
    if (next.type !== 'list') continue;
    if (node.type !== 'paragraph' && node.type !== 'heading') continue;
    if (!SOURCES_HEADER_RE.test(getNodeText(node))) continue;

    const items: SourceItem[] = [];
    for (const li of next.children || []) {
      const s = extractSourceFromListItem(li);
      if (s) items.push(s);
    }
    if (items.length === 0) continue;

    next.type = 'element';
    next.data = {
      hName: 'mdSourcesList',
      hProperties: { items: JSON.stringify(items) }
    };
    next.children = [];
  }
};

function getDomain(url: string): string {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function getFaviconUrl(url: string): string | null {
  try {
    const u = new URL(url);
    return `https://www.google.com/s2/favicons?domain=${u.hostname}&sz=64`;
  } catch {
    return null;
  }
}

interface SourcesListProps {
  items: string;
}

export const SourcesList = ({ items }: SourcesListProps) => {
  let parsed: SourceItem[];
  try {
    parsed = JSON.parse(items);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed) || parsed.length === 0) return null;

  return (
    <div className="my-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {parsed.map((item, idx) => {
        const domain = getDomain(item.url);
        const favicon = getFaviconUrl(item.url);
        return (
          <a
            key={`${item.url}-${idx}`}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-start gap-2.5 rounded-lg border border-border/60 bg-muted/30 p-2.5 no-underline transition-colors hover:border-border hover:bg-muted/60"
          >
            {favicon && (
              <img
                src={favicon}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="mt-0.5 h-4 w-4 shrink-0 rounded-sm"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.visibility =
                    'hidden';
                }}
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs text-muted-foreground">
                {domain}
              </div>
              <div className="line-clamp-2 text-sm leading-snug text-foreground group-hover:text-primary">
                {item.title}
              </div>
            </div>
          </a>
        );
      })}
    </div>
  );
};

export const sourcesComponents = {
  mdSourcesList: SourcesList
};
